"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import ProspectsList from "./ProspectsList";
import ProspectDetails from "./ProspectDetails";

export default function ProspectsTab() {
  const navigate = useNavigate();
  const { prospectId } = useParams();
  const location = useLocation();
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoadingProspect, setIsLoadingProspect] = useState(false);
  const [prospectError, setProspectError] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }
        const decoded = jwtDecode(token);

        const [prospectsResponse, usersResponse] = await Promise.all([
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/prospects",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/users",
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);

        const fetchedProspects = Array.isArray(prospectsResponse.data.prospects)
          ? prospectsResponse.data.prospects
          : [];
        setProspects(fetchedProspects);
        setUsers(
          usersResponse.data.filter(
            (user) => user.role === "project_manager" || user.role === "admin",
          ),
        );
        setLoading(false);
      } catch (err) {
        setError(
          "Failed to load data: " +
            (err.response?.data?.message || err.message),
        );
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch specific prospect when prospectId is present
  useEffect(() => {
    if (prospectId) {
      const fetchSpecificProspect = async () => {
        setIsLoadingProspect(true);
        setProspectError(null);
        setSelectedProspect(null);
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            throw new Error("No authentication token found");
          }

          const response = await axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospectId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (response.data && response.data._id) {
            setSelectedProspect(response.data);
            setProspects((prev) => {
              const exists = prev.find((p) => p._id === response.data._id);
              if (exists) {
                return prev.map((p) =>
                  p._id === response.data._id ? response.data : p,
                );
              }
              return [...prev, response.data];
            });
          } else {
            throw new Error("Prospect not found");
          }
        } catch (err) {
          console.error("Error fetching specific prospect:", err);
          setProspectError(
            err.response?.data?.message || err.message || "Prospect not found",
          );
          setSelectedProspect(null);
        } finally {
          setIsLoadingProspect(false);
        }
      };

      fetchSpecificProspect();
    } else {
      setSelectedProspect(null);
      setIsLoadingProspect(false);
      setProspectError(null);
    }
  }, [prospectId]);

  const handleSort = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  if (loading) return <div className="text-center py-4">Loading...</div>;
  if (error)
    return <div className="text-red-600 text-center py-4">{error}</div>;
  if (!Array.isArray(prospects)) {
    return (
      <div className="text-red-600 text-center py-4">
        Error: Prospects data is not available
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      {prospectId ? (
        <>
          {isLoadingProspect ? (
            <div className="text-center py-4">Loading prospect details...</div>
          ) : prospectError ||
            (!selectedProspect &&
              !prospects.find((p) => p._id === prospectId)) ? (
            <div className="text-red-600 text-center py-4">
              {prospectError || "Prospect not found"}
              <div className="mt-4">
                <button
                  onClick={() => navigate("/Prospects")}
                  className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500 ml-2"
                >
                  Back to Prospects
                </button>
              </div>
            </div>
          ) : (
            <ProspectDetails
              prospect={
                prospects.find((p) => p._id === prospectId) || selectedProspect
              }
              setProspects={setProspects}
              users={users}
              navigate={navigate}
            />
          )}
        </>
      ) : (
        <ProspectsList
          prospects={prospects}
          setProspects={setProspects}
          sortOrder={sortOrder}
          handleSort={handleSort}
          navigate={navigate}
          users={users}
        />
      )}
    </div>
  );
}
